import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ApolloError, buildUseQuery } from '@main/core-ui';
import { endpoints, parsePrivacyCenter, PrivacyCenter } from '@main/pc-types';

import { useStaticPrivacyCenterModule } from '../../hooks';
import { appendStylesheet, changeFavicon } from './helpers';
import { selectPrivacyCenter } from './selectors';
import { clearPrivacyCenter, setPrivacyCenter } from './slice';

const usePrivacyCenter = buildUseQuery(endpoints.privacyCenter);

/**
 * Hook to fetch privacy center metadata
 *
 * @returns The privacy center metadata
 */
export const usePrivacyCenterData = (): {
  /** Loading state */
  loading: boolean;
  /** Error if ocurred */
  error?: ApolloError;
  /** Parsed privacy center */
  privacyCenter?: PrivacyCenter;
  /** Whether privacy center is disabled */
  isDisabled: boolean;
} => {
  const { directData, directError, directLoading, skipGql } =
    useStaticPrivacyCenterModule<PrivacyCenter>('privacyCenter');
  const {
    data: gqlData,
    error: gqlError,
    loading: gqlLoading,
  } = usePrivacyCenter({ skip: skipGql });
  const data = skipGql ? directData : gqlData;
  const error = skipGql ? directError : gqlError;
  const loading = skipGql ? directLoading : gqlLoading;
  const privacyCenter = useSelector(selectPrivacyCenter);
  const dispatch = useDispatch();

  // Set the fonts
  useEffect(() => {
    if (privacyCenter) {
      const {
        assets: { favicon },
        theme,
      } = privacyCenter;

      // Set favicon
      if (favicon) {
        changeFavicon(favicon.src);
      }

      // Set fonts
      if (theme.textStyles.fontFamilyHeader) {
        appendStylesheet(theme.textStyles.fontFamilyHeader.url);
      }
      if (theme.textStyles.fontFamilyBody) {
        appendStylesheet(theme.textStyles.fontFamilyBody.url);
      }
    }
  }, [privacyCenter]);

  // Forward the privacy center to the redux store so that it is accessible
  // throughout the site
  useEffect(() => {
    if (data) {
      const parsedPrivacyCenter = parsePrivacyCenter(data);

      if (parsedPrivacyCenter.isDisabled) {
        dispatch(clearPrivacyCenter());
      } else {
        dispatch(
          setPrivacyCenter({
            privacyCenter: parsedPrivacyCenter,
            sombraPublicKey: data.organization.sombraPublicKeyECDH!,
            whiteLabelPrivacyCenter:
              data.organization.featureSet?.whiteLabelPrivacyCenter,
            disabledPrivacyCenterAboutTranscend:
              !!data.organization.featureSet
                ?.disabledPrivacyCenterAboutTranscend,
            workflowsV2Enabled: data.organization.featureSet?.workflowsv2,
          }),
        );
      }
    }
  }, [data, dispatch]);

  return {
    loading,
    error,
    privacyCenter,
    isDisabled: !!data?.isDisabled,
  };
};
